import { Action, RequestStatus, UseCaseStateGenerator } from '@lib/plugin-redux-core';
import {
  CreateHeadingUseCaseState,
  SaveHeadingUseCaseState,
  OpenEditHeadingUseCaseState,
  CancelHeadingUseCaseState,
  DeleteHeadingUseCaseState,
  MoveHeadingUseCaseState,
} from './heading-item.states';
import {
  CREATE_HEADING_USE_CASE,
  HeadingStatus,
  SAVE_HEADING_USE_CASE,
  SaveHeadingUseCaseInput,
  CANCEL_HEADING_USE_CASE,
  DELETE_HEADING_USE_CASE,
  MOVE_HEADING_USE_CASE,
  MoveHeadingUseCaseOutput,
  Headings,
  CreateHeadingUseCaseOutput,
  DeleteHeadingUseCaseOutput,
  NoneValue,
} from '@module/form';
import { FormEditPageState } from '../../form-edit.states';
import { OPEN_EDIT_HEADING_USE_CASE } from '../../../root.action';

export const handleCreateHeadingLocalUseCase: UseCaseStateGenerator<CreateHeadingUseCaseState> = {
  name: CREATE_HEADING_USE_CASE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    const backupHeadings = state.headings.map((heading) => heading.clone());
    return {
      ...state,
      createHeadingStatus: RequestStatus.EXECUTE,
      backupHeadings,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { newHeading, newHeadings } = action.payload as CreateHeadingUseCaseOutput;
    const selectedSection =
      state.sections.find((section) => section.id.toString() === newHeading.sectionId) ||
      state.selectedSection;

    return {
      ...state,
      selectedFormItemId: newHeading.id.toString(),
      editingFormItemId: newHeading.id.toString(),
      headings: newHeadings,
      newHeading: newHeading,
      selectedSection,
      hasUnsavedHeadingChanges: true,
      createHeadingStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      createHeadingStatus: RequestStatus.ERROR,
    };
  },
};

export const saveHeadingUseCaseHandlers: UseCaseStateGenerator<SaveHeadingUseCaseState> = {
  name: SAVE_HEADING_USE_CASE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { heading } = action.payload as SaveHeadingUseCaseInput;

    // OPTIMIZATION: Update state immediately in executing phase
    const updatedHeadings = [...state.headings].map((h) =>
      h.id.toString() === heading.id.toString() ? heading : h,
    );
    const sortedHeadings = Headings.sort(updatedHeadings);

    return {
      ...state,
      headings: sortedHeadings,
      saveHeadingStatus: RequestStatus.EXECUTE,
      hasUnsavedHeadingChanges: false,
      editingFormItemId: '', // Reset edit mode immediately
      newHeading: null,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { payload } = action;
    // State already updated in executing phase, but update with server response
    const updatedHeadings = [...state.headings].map((h) =>
      h.id.toString() === payload.id.toString() ? payload : h,
    );
    const sortedHeadings = Headings.sort(updatedHeadings);

    return {
      ...state,
      headings: sortedHeadings,
      saveHeadingStatus: RequestStatus.SUCCESS,
      hasUnsavedHeadingChanges: false,
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      saveHeadingStatus: RequestStatus.ERROR,
      hasUnsavedHeadingChanges: false,
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      saveHeadingStatus: RequestStatus.RESET,
      hasUnsavedHeadingChanges: false,
      editingFormItemId: '',
    };
  },
};

export const handleOpenEditHeading: UseCaseStateGenerator<OpenEditHeadingUseCaseState> = {
  name: OPEN_EDIT_HEADING_USE_CASE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    const headingId = action.payload;
    const { headings } = state;
    const foundHeading = headings.find((h) => h.id.toString() === headingId);
    // Make sure we have a clean clone with all properties for form editing
    let revertedHeading = null;
    if (foundHeading) {
      revertedHeading = foundHeading.clone();
    }

    return {
      ...state,
      // Opening edit is not an unsaved change; mark dirty only when the heading is modified.
      hasUnsavedHeadingChanges: false,
      revertedHeading,
      editingHeadingId: foundHeading?.id.toString() || '',
      selectedFormItemId: foundHeading?.id.toString() || '',
      editingFormItemId: foundHeading?.id.toString() || '',
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      revertedHeading: null,
      editingHeadingId: '',
      editingFormItemId: '',
    };
  },
};

export const handleCancelHeading: UseCaseStateGenerator<CancelHeadingUseCaseState> = {
  name: CANCEL_HEADING_USE_CASE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    return {
      ...state,
      cancelHeadingStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { payload } = action;
    const output = payload; // CancelHeadingUseCaseOutput
    const { canceledHeading, nextHeading, previousHeading } = output;

    // Determine the previous item for selection after cancel
    const previousItemOfCanceledHeadingId =
      canceledHeading.previousHeadingId === NoneValue
        ? state.selectedSection?.id.toString() || ''
        : canceledHeading.previousHeadingId;

    // Case 1: New heading that should be removed (status = "Deactivate")
    if (canceledHeading.status === HeadingStatus.Deactivate) {
      // Prefer full restore from backup so create/cancel/create keeps link integrity.
      // Surgical relink alone left previousHeading.nextHeadingId pointing at the removed id.
      if (state.backupHeadings?.length) {
        return {
          ...state,
          headings: state.backupHeadings.map((heading) => heading.clone()),
          backupHeadings: null,
          newHeading: null,
          revertedHeading: null,
          editingFormItemId: '',
          selectedFormItemId: previousItemOfCanceledHeadingId,
          hasUnsavedHeadingChanges: false,
          cancelHeadingStatus: RequestStatus.SUCCESS,
          createHeadingStatus: RequestStatus.RESET,
        };
      }

      const newHeadings = state.headings.filter(
        (h) => h.id.toString() !== canceledHeading.id.toString(),
      );

      if (previousHeading) {
        const previousHeadingIndex = newHeadings.findIndex(
          (h) => h.id.toString() === previousHeading.id.toString(),
        );
        if (previousHeadingIndex !== -1) {
          newHeadings[previousHeadingIndex] = previousHeading.clone();
        }
      }

      if (nextHeading) {
        const nextHeadingIndex = newHeadings.findIndex(
          (h) => h.id.toString() === nextHeading.id.toString(),
        );
        if (nextHeadingIndex !== -1) {
          newHeadings[nextHeadingIndex] = nextHeading.clone();
        }
      }

      return {
        ...state,
        headings: newHeadings,
        backupHeadings: null,
        newHeading: null,
        revertedHeading: null,
        editingFormItemId: '',
        selectedFormItemId: previousItemOfCanceledHeadingId,
        hasUnsavedHeadingChanges: false,
        cancelHeadingStatus: RequestStatus.SUCCESS,
        createHeadingStatus: RequestStatus.RESET,
      };
    }

    // Case 2: Existing heading - restore original data
    const headings = [...state.headings];
    const headingIndex = headings.findIndex(
      (h) => h.id.toString() === canceledHeading.id.toString(),
    );

    if (headingIndex !== -1) {
      headings[headingIndex] = canceledHeading;
    }

    return {
      ...state,
      headings,
      revertedHeading: null,
      editingFormItemId: '',
      hasUnsavedHeadingChanges: false,
      cancelHeadingStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      cancelHeadingStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      cancelHeadingStatus: RequestStatus.RESET,
      editingFormItemId: '',
      hasUnsavedHeadingChanges: false,
    };
  },
};

export const handleDeleteHeading: UseCaseStateGenerator<DeleteHeadingUseCaseState> = {
  name: DELETE_HEADING_USE_CASE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    return {
      ...state,
      deleteHeadingId: action.payload.headingId,
      deleteHeadingStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { newHeadings } = action.payload as DeleteHeadingUseCaseOutput;
    return {
      ...state,
      headings: newHeadings,
      deleteHeadingStatus: RequestStatus.SUCCESS,
      selectedFormItemId: state.selectedSection?.id.toString() || '',
      editingFormItemId: '',
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { error } = action;
    return {
      ...state,
      error,
      deleteHeadingStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      deleteHeadingStatus: RequestStatus.RESET,
    };
  },
};

export const handleMoveHeadingUseCase: UseCaseStateGenerator<MoveHeadingUseCaseState> = {
  name: MOVE_HEADING_USE_CASE,
  executing: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      moveHeadingStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { headings } = action.payload as MoveHeadingUseCaseOutput;
    // Do not run Headings.sort on the full list — multiple sections each have a
    // previousHeadingId="none" head, and a global sort would drop other sections.
    // Per-section sort happens at render time (sections / sidebar containers).
    return {
      ...state,
      headings,
      moveHeadingStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    return {
      ...state,
      error: action.error,
      moveHeadingStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      moveHeadingStatus: RequestStatus.RESET,
    };
  },
};

export const headingItemHandlers = [
  handleCreateHeadingLocalUseCase,
  saveHeadingUseCaseHandlers,
  handleOpenEditHeading,
  handleCancelHeading,
  handleDeleteHeading,
  handleMoveHeadingUseCase,
];
